import * as React from 'react';
import styled from '@independent-software/typeui/styles/Theme'

import { Category } from './Category';
import { List } from '@independent-software/typeui/controls/List';
import { Icon } from '@independent-software/typeui/controls/Icon';
import { Dialog } from '@independent-software/typeui/controls/Dialog';
import { Form } from '@independent-software/typeui/controls/Form';
import { Input } from '@independent-software/typeui/controls/Input';
import { Button } from '@independent-software/typeui/controls/Button';

type TMode = 'none' | 'add' | 'edit' | 'delete';

interface ICategoryItem {
  id?: number;
  parent: number;
  name: string;
}

interface IProps {
  /** Tree of items to show. Each item must have a parent (numeric) that points
   *  to its parent, or null. */
  items: ICategoryItem[];
  /**
   * Fired when item is clicked.
   */
  onClick?: (item: ICategoryItem) => void;
  /**
   * Fired when a new item is added to a parent.
   */
  onAdd?: (item: ICategoryItem, name: string) => void;
  /**
   * Fired when an item is edited.
   */
  onEdit?: (item: ICategoryItem, name: string) => void;
  /**
   * Fired when an item is deleted.
   */
  onDelete?: (item: ICategoryItem) => void;
  /**
   * If a child count must be shown, the name of the counter
   * field (e.g. stations_count) must be provided.
   */
  countfield?: string;
  /**
   * Array of items IDs that are active, to be highlighted
   * in the tree.
   */
  active?: number[];
}

interface IState {
  mode: TMode;
  open: Set<number>;
  current: ICategoryItem;
  name: string;
}

class CategoryTree extends React.Component<IProps, IState> {

  constructor(props: IProps) {
    super(props);
    this.state = {
      mode: 'none',
      open: new Set<number>(),
      current: null,
      name: ''
    }
  }

  private isActive = (id: number) => {
    return this.props.active && this.props.active.includes(id);
  }

  private toggleItem = (id: number) => {
    if(this.state.open.has(id)) {
      this.state.open.delete(id);
    } else {
      this.state.open.add(id);
    }
    this.setState({ 
      open: this.state.open
    });
  }

  private  handleChangeName = (name: string) => {
    this.setState({ name: name });
  }  

  private handleBeginAdd = (item: ICategoryItem) => {
    if(item) {
      this.state.open.add(item.id);
      this.setState({ open: this.state.open });
    }
    this.setState({ current: item, name: '', mode: 'add' });    
  }

  private handleEndAdd = () => {
    this.setState({ mode: 'none' });
    this.props.onAdd(this.state.current, this.state.name);
  }

  private handleBeginEdit = (item: ICategoryItem) => {
    this.setState({ current: item, name: item.name, mode: 'edit' });    
  }

  private handleEndEdit = () => {
    this.setState({ mode: 'none' });
    this.props.onEdit(this.state.current, this.state.name);
  }  

  private handleBeginDelete = (item: ICategoryItem) => {
    this.setState({ current: item, mode: 'delete' }); 
  }

  private handleEndDelete = () => {
    this.setState({ mode: 'none' });
    this.props.onDelete(this.state.current);
  }

  private renderItem = (item: ICategoryItem): React.ReactNode => {
    let p = this.props;
    // Does this item have any children?
    let hasChildren = this.props.items
      .some((sc) => sc.parent == item.id);
    // Is item currently open?
    let isOpen = this.state.open.has(item.id);

    return (
      <List.Item key={item.id}>
        <IconHolder>
          {hasChildren && <Icon name="caret-down" rotated={isOpen ? 0 : -90} onClick={() => this.toggleItem(item.id)}/>}
          {!hasChildren && <Icon name="circle" color="#fff"/>}
        </IconHolder>
        <List.Content>
          <Category 
            active={this.isActive(item.id)}
            onAdd={p.onAdd ? () => this.handleBeginAdd(item) : null} 
            onEdit={p.onEdit ? () => this.handleBeginEdit(item) : null} 
            onDelete={p.onDelete ? () => this.handleBeginDelete(item) : null} 
            onClick={this.props.onClick ? () => this.props.onClick(item) : null}>
              {item.name}
              {p.countfield && (item as any)[p.countfield] > 0 && <Count>{(item as any)[p.countfield]}</Count>}
          </Category>
          {isOpen && this.renderList(item.id)}
        </List.Content>
      </List.Item>
    );
  }

  private renderList = (parent: number = null): React.ReactNode => {
    return (
      <List relaxed>
        {this.props.items
          .filter(item => item.parent == parent)
          .map(item => this.renderItem(item))}
      </List>
    );
  }

  render = () => {
    return (
      <>
        {this.renderList()}
        
        {this.props.onAdd && <ButtonHolder>
          <Button icon primary size="small" onClick={() => this.handleBeginAdd(null)}><Icon name="plus"/>Add</Button>
        </ButtonHolder>}

        <Dialog open={this.state.mode == 'add'} onClose={() => this.setState({mode: 'none'})}>
          <Dialog.Content>
            <Form.Uncontrolled label="Category name" hint="Please enter a category name.">
              <Input value={this.state.name} type="text" placeholder="Name" fluid onChange={this.handleChangeName}/>
            </Form.Uncontrolled>
          </Dialog.Content>
          <Dialog.Footer>
            <Button secondary onClick={() => this.setState({mode: 'none'})}>Cancel</Button>
            <Button primary disabled={this.state.name.length == 0} onClick={this.handleEndAdd}>Save</Button>
          </Dialog.Footer>
        </Dialog>

        <Dialog open={this.state.mode == 'edit'} onClose={() => this.setState({mode: 'none'})}>
          <Dialog.Content>
            <Form.Uncontrolled label="Category name" hint="Please enter a category name.">
              <Input value={this.state.name} type="text" placeholder="Name" fluid onChange={this.handleChangeName}/>
            </Form.Uncontrolled>
          </Dialog.Content>
          <Dialog.Footer>
            <Button secondary onClick={() => this.setState({mode: 'none'})}>Cancel</Button>
            <Button primary disabled={this.state.name.length == 0} onClick={this.handleEndEdit}>Update</Button>
          </Dialog.Footer>
        </Dialog>

        <Dialog open={this.state.mode == 'delete'} onClose={() => this.setState({mode: 'none'})}>
          <Dialog.Content>
            <p>
              Are you sure you wish to <strong>delete</strong> this category?
            </p>
            <p>
              <strong>This will also delete all subcategories.</strong>
            </p>
          </Dialog.Content>
          <Dialog.Footer>
            <Button secondary onClick={() => this.setState({mode: 'none'})}>Cancel</Button>
            <Button negative onClick={this.handleEndDelete}>Delete</Button>
          </Dialog.Footer>
        </Dialog>        

      </>
    );
  }
}

const IconHolder = styled('div')`
  padding-top: 2px;
`

const ButtonHolder = styled('div')`
  padding: 6px 0 0 28px;
`

const Count = styled('div')`
  display: inline-block;
  font-size: 80%;
  margin-left: 8px;
  color: #fff;
  background: #666;
  padding: 0 6px 0 6px;
  line-height: 1.3em;
`

export { CategoryTree }
